'use client';

import { useCallback, useMemo } from 'react';

type Option<T> = { label: string; value: T };
type Comparator<T> = (option: Option<T>, handle: T | null) => boolean;
type ToLabel<U> = (handle: U) => string;
type ToValue<U, V> = (handle: U) => V;

const useOptions = <U, V>(
  entries: U[],
  initialEntry: V | null,
  selectedEntry: V | null,
  toLabel: ToLabel<U>,
  toValue: ToValue<U, V>,
  comparator: Comparator<V> = (option, selectedEntry) =>
    option.value === selectedEntry
) => {
  const optionMaker = useCallback(
    (entry: U) => ({ label: toLabel(entry), value: toValue(entry) }),
    [toLabel, toValue]
  );

  const options = useMemo(
    () => entries.map(optionMaker),
    [entries, optionMaker]
  );

  const selectedOption = useMemo(
    () => options.find((option) => comparator(option, selectedEntry)),
    [options, selectedEntry, comparator]
  );

  const initialOption = useMemo(
    () => options.find((option) => comparator(option, initialEntry)),
    [options, initialEntry, comparator]
  );

  return useMemo(
    () => ({ options, selectedOption, initialOption }),
    [options, selectedOption, initialOption]
  );
};

export default useOptions;
